// Loads pilots for the Kavaliers / Chassis pages.
//
// Pilots are hand-written Markdown files in src/data/pilots/*.md: a YAML block
// between --- lines holds the IDENT/mech details, and the Markdown below it is
// the bio (## Appearance, ## History, ...). See _TEMPLATE.md for every field.

import yaml from "js-yaml";
import { all } from "@/utils/lancerData";
import { findBond, stripHtml } from "@/utils/lancerInfo";

const frames = all("frames");

const asList = value => (value == null ? [] : Array.isArray(value) ? value : [value]);
// Default image paths by callsign: portraits /pilots/CALLSIGN.webp, mechs /mechs/CALLSIGN.png
const defaultArt = (folder, callsign, ext) => `/${folder}/${callsign.toUpperCase()}.${ext}`;

// A stable, UUID-shaped record number derived from the callsign, for pilots
// without an explicit `id:`.
function identFromCallsign(callsign) {
	let hex = "";
	let h = 2166136261;
	for (let round = 0; hex.length < 32; round++) {
		for (const ch of `${callsign}#${round}`) {
			h ^= ch.charCodeAt(0);
			h = Math.imul(h, 16777619) >>> 0;
		}
		hex += h.toString(16).padStart(8, "0");
	}
	return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
		16,
		20
	)}-${hex.slice(20, 32)}`;
}

// Match "GMS Everest", "Everest" or "IPS-N Blackbeard" against the frame catalog.
function findFrame(name) {
	if (!name) return null;
	const wanted = String(name).trim().toLowerCase();
	return (
		frames.find(f => `${f.source} ${f.name}`.toLowerCase() === wanted) ||
		frames.find(f => f.name.toLowerCase() === wanted) ||
		null
	);
}

// "Assault Rifle // Main" -> { name: "Assault Rifle", mount: "Main" }. Entries
// can also be { name, mount, description } for gear the data doesn't have.
function parseWeapon(entry) {
	if (entry && typeof entry === "object")
		return {
			name: entry.name || "",
			mount: entry.mount || "",
			description: entry.description || "",
		};
	const [name, mount = ""] = String(entry)
		.split("//")
		.map(part => part.trim());
	return { name, mount, description: "" };
}

// "EVA Module" or { name, description }
function parseSystem(entry) {
	if (entry && typeof entry === "object")
		return { name: entry.name || "", description: entry.description || "" };
	return { name: String(entry), description: "" };
}

function manualMech(m, callsign) {
	if (!m) return null;
	const frame = findFrame(m.frame);
	const mechtype =
		m.role ||
		(Array.isArray(frame?.mechtype) ? frame.mechtype.join(" // ") : frame?.mechtype) ||
		"";
	return {
		id: m.id || identFromCallsign(`${callsign}:${m.name}`),
		name: m.name || "UNREGISTERED",
		manufacturer: m.manufacturer || frame?.source || "",
		frameName: frame?.name || m.frame || "",
		// Frames whose license covers this one: itself, plus the base frame of an
		// alternate (Enkidu → Tokugawa via `variant`, SotW alts via `license_id`)
		licenseFrames: [
			frame?.name || m.frame,
			frame?.variant,
			frames.find(f => f.id === frame?.license_id && f.id !== frame?.id)?.name,
		].filter(Boolean),
		mechtype,
		description: m.description || frame?.description || "",
		// Frame baseline from the Lancer data, with any `stats:` overrides on top
		stats: { ...(frame?.stats || {}), ...(m.stats || {}) },
		traits: m.traits ? asList(m.traits) : (frame?.traits || []).map(t => t.name),
		core: m.core || frame?.core_system?.name || "",
		weapons: asList(m.weapons).map(parseWeapon),
		systems: asList(m.systems).map(parseSystem),
		art: m.art || defaultArt("mechs", callsign, "png"),
	};
}

// KTB bond: the pilot file names the bond and records the pilot's choices
// (minor ideal, powers, question answers) and progress; the major ideals,
// power descriptions and question text come from the KTB data. Bonds missing
// from the data (e.g. homebrew) can supply `major_ideals`, `questions` and
// powers as `{ name, description }` in the file instead.
function manualBond(b) {
	if (!b) return null;
	const bond = typeof b === "string" ? { name: b } : b;
	if (!bond.name) return null;
	const data = findBond(bond.name);
	const questions = bond.questions
		? asList(bond.questions)
		: (data?.questions || []).map(q => q.question);
	return {
		name: data?.name || bond.name,
		xp: bond.xp ?? null,
		stress: bond.stress ?? null,
		// KTB: you break when taking stress at maximum (typically 8; some powers raise it)
		stressMax: Number(bond.stress_max) || 8,
		majorIdeals: bond.major_ideals ? asList(bond.major_ideals) : data?.major_ideals || [],
		minorIdeal: bond.minor_ideal || "",
		powers: asList(bond.powers).map(power => {
			const name = typeof power === "object" ? power.name : power;
			const known = data?.powers.find(p => p.name.toLowerCase() === String(name).toLowerCase());
			return {
				name,
				description: stripHtml(
					(typeof power === "object" && power.description) || known?.description
				),
			};
		}),
		burdens: asList(bond.burdens).map(burden =>
			typeof burden === "object"
				? { name: burden.name, progress: burden.progress ?? 0, segments: burden.segments ?? null }
				: { name: burden, progress: 0, segments: null }
		),
		answers: asList(bond.answers).map((answer, i) => ({ question: questions[i] || "", answer })),
	};
}

// Stress / XP / burdens for the Status "Pilots" tab — read from the bond block
// even before a bond is chosen, so the tracks can be kept from the start
function bondCondition(b) {
	const bond = b && typeof b === "object" ? b : {};
	return {
		stress: Number(bond.stress) || 0,
		stressMax: Number(bond.stress_max) || 8,
		xp: Number(bond.xp) || 0,
		burdens: asList(bond.burdens).map(burden =>
			typeof burden === "object"
				? { name: burden.name, progress: burden.progress ?? 0, segments: burden.segments ?? null }
				: { name: burden, progress: 0, segments: null }
		),
	};
}

function parseManual(raw, file) {
	const text = raw.replace(/\r\n/g, "\n");
	const match = text.match(/^---\n([\s\S]*?)\n---\n?([\s\S]*)$/);
	let meta;
	try {
		meta = (match && yaml.load(match[1])) || {};
	} catch (err) {
		// One malformed file (usually indentation) shouldn't take every pilot down with it
		console.error(`Skipping pilot file ${file}: ${err.message}`);
		return null;
	}
	const callsign = String(meta.callsign || "").trim();
	if (!callsign) return null;
	const hase = meta.hase || {};
	const gear = meta.gear || {};
	return {
		id: meta.id || identFromCallsign(callsign),
		callsign,
		name: meta.name || "",
		player: meta.player || "",
		temporary: meta.temporary === true,
		background: meta.background || "",
		level: Number(meta.level) || 0,
		updated: meta.updated || null,
		hase: [hase.hull, hase.agility, hase.systems, hase.engineering].map(v => Number(v) || 0),
		// "Hack or Fix +2", or { name, description } for custom triggers
		skills: asList(meta.skills).map(skill =>
			skill && typeof skill === "object"
				? { name: String(skill.name || ""), description: skill.description || "" }
				: { name: String(skill), description: "" }
		),
		talents: asList(meta.talents),
		licenses: asList(meta.licenses),
		gear: { armor: asList(gear.armor), weapons: asList(gear.weapons), items: asList(gear.items) },
		bio: match ? match[2].trim() : text.trim(),
		portrait: meta.portrait || defaultArt("pilots", callsign, "webp"),
		mech: manualMech(meta.mech, callsign),
		bond: manualBond(meta.bond),
		condition: bondCondition(meta.bond),
		clocks: asList(meta.clocks),
		reserves: asList(meta.reserves),
	};
}

/**
 * A mech as Foundry has it (the pilot's active mech, from liveState.js): built like a pilot file's mech, from the frame
 * data, with Foundry's name, frame and loadout, and the file's art and id kept (Foundry's images aren't public).
 */
export function mechFromFoundry(data, callsign, base = null) {
	const mech = manualMech(
		{
			id: base?.id,
			name: data.name,
			frame: data.frame || base?.frameName,
			weapons: (data.weapons || []).map(weapon => ({
				name: weapon.name,
				mount: weapon.mount || "",
			})),
			systems: data.systems || [],
			art: base?.art,
		},
		callsign
	);
	return { ...mech, condition: data.condition || null };
}

const pilotFiles = import.meta.glob("@/data/pilots/*.md", {
	query: "?raw",
	import: "default",
	eager: true,
});

// Alphabetical by callsign, with placeholder (`temporary: true`) pilots last
export const pilots = Object.entries(pilotFiles)
	.map(([file, raw]) => parseManual(raw, file))
	.filter(Boolean)
	.sort(
		(a, b) =>
			Number(!!a.temporary) - Number(!!b.temporary) ||
			a.callsign.localeCompare(b.callsign, undefined, { sensitivity: "base" })
	);
